import React, { useState, useEffect } from 'react'
import { Suggestion } from '../../types/mockup'
import './CascadeVisualization.css'

interface CascadeVisualizationProps {
  suggestion: Suggestion
  isActive?: boolean
}

type StepStatus = 'pending' | 'in_progress' | 'completed' | 'failed'

interface CascadeStep {
  id: string
  title: string
  description: string
  estimatedDuration: number
  dependencies: string[]
  status: StepStatus
  startTime?: Date
  completedTime?: Date
  errorMessage?: string
}

export const CascadeVisualization: React.FC<CascadeVisualizationProps> = ({
  suggestion,
  isActive = false
}) => {
  const [steps, setSteps] = useState<CascadeStep[]>([])
  const [currentStep, setCurrentStep] = useState<number>(0)
  const [isPlaying, setIsPlaying] = useState(false)

  // Initialize cascade steps based on suggestion
  useEffect(() => {
    const cascadeSteps: CascadeStep[] = [
      {
        id: 'validate',
        title: 'Validate Changes',
        description: 'Check for conflicts and validate all proposed changes',
        estimatedDuration: 30,
        dependencies: [],
        status: 'pending'
      },
      {
        id: 'notify_vendors',
        title: 'Notify Vendors',
        description: `Send notifications to ${suggestion.operationalImpact.affectedVendors.length} affected vendors`,
        estimatedDuration: 60,
        dependencies: ['validate'],
        status: 'pending'
      },
      {
        id: 'update_timeline',
        title: 'Update Timeline',
        description: `Apply ${suggestion.operationalImpact.timelineChanges.length} timeline changes`,
        estimatedDuration: 45,
        dependencies: ['validate'],
        status: 'pending'
      },
      {
        id: 'confirm_vendors',
        title: 'Confirm with Vendors',
        description: 'Wait for vendor confirmations and resolve conflicts',
        estimatedDuration: 180,
        dependencies: ['notify_vendors', 'update_timeline'],
        status: 'pending'
      },
      {
        id: 'finalize',
        title: 'Finalize Changes',
        description: 'Apply all confirmed changes and update stakeholders',
        estimatedDuration: 30,
        dependencies: ['confirm_vendors'],
        status: 'pending'
      }
    ]

    setSteps(cascadeSteps)
  }, [suggestion])

  // Simulate cascade progression
  useEffect(() => {
    if (!isActive || !isPlaying) return

    const interval = setInterval(() => {
      setSteps(prevSteps => {
        const updated = [...prevSteps]
        
        // Find next step to progress
        for (let i = 0; i < updated.length; i++) {
          const step = updated[i]
          
          if (step.status === 'pending') {
            // Check if dependencies are met
            const depsMet = step.dependencies.every(depId => 
              updated.find(s => s.id === depId)?.status === 'completed'
            )
            
            if (depsMet) {
              updated[i] = {
                ...step,
                status: 'in_progress',
                startTime: new Date()
              }
              setCurrentStep(i)
              break
            }
          } else if (step.status === 'in_progress') {
            // Simulate completion after some time
            const elapsed = Date.now() - (step.startTime?.getTime() || 0)
            if (elapsed > step.estimatedDuration * 10) { // 10ms per second for demo
              updated[i] = {
                ...step,
                status: 'completed',
                completedTime: new Date()
              }
            }
            break
          }
        }
        
        return updated
      })
    }, 100)

    return () => clearInterval(interval)
  }, [isActive, isPlaying])

  // Start/stop simulation
  const toggleSimulation = () => {
    if (isPlaying) {
      setIsPlaying(false)
    } else {
      setIsPlaying(true)
    }
  }

  // Reset simulation
  const resetSimulation = () => {
    setIsPlaying(false)
    setCurrentStep(0)
    setSteps(prev => prev.map(step => ({
      ...step,
      status: 'pending',
      startTime: undefined,
      completedTime: undefined,
      errorMessage: undefined
    })))
  }

  // Get step icon
  const getStepIcon = (status: StepStatus): string => {
    switch (status) {
      case 'pending': return '⏳'
      case 'in_progress': return '🔄'
      case 'completed': return '✅'
      case 'failed': return '❌'
      default: return '⭕'
    }
  }

  // Get step color
  const getStepColor = (status: StepStatus): string => {
    switch (status) {
      case 'pending': return '#9aa0a6'
      case 'in_progress': return '#1a73e8'
      case 'completed': return '#34a853'
      case 'failed': return '#ea4335'
      default: return '#9aa0a6'
    }
  }

  // Format duration
  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    return `${minutes}m ${seconds % 60}s`
  }

  // Calculate total progress
  const totalProgress = steps.length > 0 
    ? (steps.filter(s => s.status === 'completed').length / steps.length) * 100
    : 0

  const isComplete = steps.every(s => s.status === 'completed')

  return (
    <div className="cascade-visualization">
      {/* Header */}
      <div className="cascade-header">
        <h3 className="cascade-title">Change Implementation Process</h3>
        <p className="cascade-description">
          This shows the step-by-step process for implementing the suggested changes, 
          including dependencies and estimated timelines.
        </p>
        
        {/* Progress Bar */}
        <div className="overall-progress">
          <div className="progress-header">
            <span className="progress-label">Overall Progress</span>
            <span className="progress-percentage">{Math.round(totalProgress)}%</span>
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${totalProgress}%` }}
            />
          </div>
        </div>

        {/* Controls */}
        <div className="cascade-controls">
          <button 
            className={`btn-simulate ${isPlaying ? 'playing' : ''}`}
            onClick={toggleSimulation}
            disabled={isComplete}
          >
            {isPlaying ? '⏸️ Pause' : '▶️ Simulate'}
          </button>
          <button 
            className="btn-reset"
            onClick={resetSimulation}
          >
            🔄 Reset
          </button>
        </div>
      </div>

      {/* Steps Visualization */}
      <div className="cascade-steps">
        {steps.map((step, index) => (
          <div key={step.id} className={`cascade-step ${step.status}`}>
            {/* Step Connector */}
            <div className="step-connector">
              <div 
                className="step-dot"
                style={{ backgroundColor: getStepColor(step.status) }}
              >
                <span className="step-icon">{getStepIcon(step.status)}</span>
              </div>
              {index < steps.length - 1 && (
                <div 
                  className={`connector-line ${
                    steps[index + 1].status !== 'pending' ? 'active' : ''
                  }`}
                />
              )}
            </div>

            {/* Step Content */}
            <div className="step-content">
              <div className="step-header">
                <h4 className="step-title">{step.title}</h4>
                <div className="step-meta">
                  <span className="step-duration">
                    ~{formatDuration(step.estimatedDuration)}
                  </span>
                  {step.status === 'in_progress' && (
                    <span className="step-spinner">⟳</span>
                  )}
                </div>
              </div>
              
              <p className="step-description">{step.description}</p>
              
              {/* Dependencies */}
              {step.dependencies.length > 0 && (
                <div className="step-dependencies">
                  <span className="deps-label">Depends on:</span>
                  <div className="deps-list">
                    {step.dependencies.map(depId => {
                      const depStep = steps.find(s => s.id === depId)
                      return depStep ? (
                        <span 
                          key={depId}
                          className={`dep-item ${depStep.status}`}
                        >
                          {depStep.title}
                        </span>
                      ) : null
                    })}
                  </div>
                </div>
              )}

              {/* Timing Info */}
              {(step.startTime || step.completedTime) && (
                <div className="step-timing">
                  {step.startTime && (
                    <span className="timing-item">
                      Started: {step.startTime.toLocaleTimeString()}
                    </span>
                  )}
                  {step.completedTime && (
                    <span className="timing-item">
                      Completed: {step.completedTime.toLocaleTimeString()}
                    </span>
                  )}
                </div>
              )}

              {/* Error Message */}
              {step.errorMessage && (
                <div className="step-error">
                  <span className="error-icon">⚠️</span>
                  {step.errorMessage}
                </div>
              )}

              {/* Progress Bar for Active Step */}
              {step.status === 'in_progress' && (
                <div className="step-progress">
                  <div className="step-progress-bar">
                    <div className="step-progress-fill" />
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Summary */}
      <div className="cascade-summary">
        <div className="summary-stats">
          <div className="stat-item">
            <span className="stat-label">Total Steps:</span>
            <span className="stat-value">{steps.length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Estimated Time:</span>
            <span className="stat-value">
              {formatDuration(steps.reduce((sum, step) => sum + step.estimatedDuration, 0))}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Parallel Tasks:</span>
            <span className="stat-value">
              {Math.max(...steps.map(step => step.dependencies.length + 1))}
            </span>
          </div>
        </div>

        {isComplete && (
          <div className="completion-message">
            <span className="completion-icon">🎉</span>
            <span className="completion-text">
              All changes have been successfully implemented!
            </span>
          </div>
        )}
      </div>

      {/* Implementation Notes */}
      <div className="implementation-notes">
        <h4>📋 Implementation Notes</h4>
        <ul>
          <li>Vendor notifications are sent simultaneously to minimize delays</li>
          <li>Timeline changes are applied immediately upon validation</li>
          <li>The system waits for vendor confirmations before finalizing</li>
          <li>Failed steps can be retried or handled manually</li>
          <li>All changes are logged for audit and rollback purposes</li>
        </ul>
      </div>
    </div>
  )
}

export default CascadeVisualization